/**
 * 把后端 detail 摊平成「人能读的一句话」。
 *
 * 为什么需要它（2026-10-22 线上事故，连续两次）：
 *   用户在设计项目详情点上传，整页白屏。Playwright 复现拿到的真实异常是
 *   `Objects are not valid as a React child (found: object with keys
 *   {error, message, currentKey, currentName, stageKey})`。
 *
 * 根因：后端有几处用 `HTTPException(400, detail={...})`（阶段顺序门禁、
 * 黄卡门禁、门禁缺文件），detail 是**字典**而不是字符串。而前端把 detail
 * 当字符串用，一共有三条通道会把对象漏到 React 手里：
 *   ① `toast.error(e?.response?.data?.detail || e.message)` —— 约 1200 处
 *   ② `setErr(e?.detailText || e.message)` 再 `{err}` 渲染
 *   ③ `setD({ error: ... })` 再 `{d.error}` 渲染
 * 任一条漏过去，React 渲染期抛错都会**卸掉整棵树**（#root 子节点变 0），
 * 用户看到的就是白屏 —— 比看不到报错更糟。
 *
 * 所以这里提供唯一一份实现，三条通道共用：
 *   · `components/ui/sonner.jsx` 的 toast 封装（通道①）
 *   · `lib/api.js` 的响应错误拦截器（给 error 挂一个 `detailText`，通道②③的 catch 用它）
 *   · `errText(e)` 给仍然要渲染错误对象的 JSX 用（通道②③的渲染处兜底）
 *
 * 口径与 lib/api.js 的 uploadErrorText、sonner 的 flattenToastContent 保持一致：
 * 对象优先取 message，其次把已知字段拼起来，最后兜底 JSON。
 */

const isReactNode = (v) => !!(v && typeof v === "object" && v.$$typeof);

/**
 * detail → 可直接交给 React 渲染 / 直接显示的东西。
 * 传入 React 元素或元素数组时原样返回（那是合法子节点，不能碰）。
 */
export function detailToText(v) {
  if (v == null || typeof v === "string" || typeof v === "number") return v;
  if (isReactNode(v)) return v;
  if (Array.isArray(v)) {
    // 元素数组是合法子节点；普通数组摊平拼接。
    return v.some(isReactNode) ? v : v.map(detailToText).filter((x) => x !== "" && x != null).join("、");
  }
  if (typeof v !== "object") return String(v);

  // 结构化 detail：message 是最贴近用户的一句话。
  if (typeof v.message === "string" && v.message) return v.message;

  // 门禁类：把「缺什么 / 卡在哪」拼出来，避免只剩一句笼统的失败。
  const bits = [];
  const push = (x) => {
    if (typeof x === "string" && x) bits.push(x);
    else if (x && typeof x === "object") {
      const label = [x.task, x.name, x.key].find((s) => typeof s === "string" && s) || "";
      const lack = Array.isArray(x.missing) ? `缺 ${x.missing.join("、")}` : "";
      if (label || lack) bits.push([label, lack].filter(Boolean).join(" "));
    }
  };
  if (Array.isArray(v.detail)) v.detail.forEach(push);
  else if (Array.isArray(v.missing)) push({ missing: v.missing });
  else if (Array.isArray(v.missingKinds)) push({ missing: v.missingKinds });
  const flat = bits.filter(Boolean).join("；");
  if (flat) return flat;

  // `error` 字段多半是机器码（stage_order_violation / yellow_card_missing），
  // 直接抛给用户等于没提示。只在它看起来像一句话时才当文案用。
  if (typeof v.error === "string" && /[\s\u4e00-\u9fa5]/.test(v.error)) return v.error;

  try {
    const s = JSON.stringify(v);
    return s && s !== "{}" ? s : String(v);
  } catch {
    return String(v);
  }
}

/**
 * axios error → 一句话。给 JSX 渲染用：任何形状的 error 都不会再炸掉整页。
 * 用法：`{d.error ? <p>{errText({ response: { data: { detail: d.error } } })}</p> : null}`
 * 或者更常见的，catch 里已经存了 detail 本身：`errTextOf(d.error)`。
 */
export function errText(e) {
  if (e == null) return "";
  const det = e?.response?.data?.detail;
  const t = detailToText(det);
  if (t != null && t !== "") return typeof t === "string" ? t : t;
  return e?.message || e?.response?.statusText || String(e);
}

/** 已经取到 detail（可能是对象）时用它，等价于 errText 但不套 error 壳。 */
export const errTextOf = (detail, fallback = "") => {
  const t = detailToText(detail);
  if (t == null || t === "") return fallback;
  return typeof t === "string" ? t : t;
};

/**
 * 【源头治理】把响应体里的**对象** detail 就地摊平成字符串，原对象备份到 `detailRaw`。
 *
 * 为什么不逐个改调用点（2026-10-22 的教训）：
 *   全站 `e?.response?.data?.detail` 出现 1212 次，其中进 state / 进 JSX 的形态各异
 *   （`setXxx(...)`、`patchQ(...)`、`setD({error: ...})`、`const d = ...` 再分支……）。
 *   逐条改的结果是：改漏了 `patchQ`（真根因），又把 `const d = detail` 之后依赖
 *   `d.error === "visit_sla"` 的地方改成了字符串（功能被改坏）。**改不干净，也改不安全。**
 *
 * 所以改成在**唯一入口**（axios 响应错误拦截器）处理：
 *   · detail 是字符串 / 数字 → 原样不动，99% 的场景行为完全不变
 *   · detail 是对象 / 数组     → `data.detailRaw` 存原对象，`data.detail` 换成可读文本
 *
 * 效果：所有 1212 处 `e?.response?.data?.detail` 拿到的天然就是字符串，
 * 一行都不用改，也不可能再漏。少数确实要看对象字段的（visit_sla / missing /
 * missingKinds / 门禁 currentKey）显式读 `detailRaw`。
 *
 * 幂等：重复调用不会二次包装（detail 已是字符串就直接返回）。
 */
export function sanitizeDetail(error) {
  const data = error?.response?.data;
  if (!data || typeof data !== "object") return;
  const det = data.detail;
  if (det == null || typeof det === "string" || typeof det === "number") return;
  let text;
  try {
    text = detailToText(det);
  } catch {
    return;
  }
  if (typeof text !== "string" || !text) return;
  try {
    if (data.detailRaw === undefined) data.detailRaw = det;
    data.detail = text;
    data.detailText = text;
    error.detailText = text;
    if (error.detailRaw === undefined) error.detailRaw = det;
  } catch {
    /* 响应体可能被冻结，挂不上就算了 —— 渲染侧还有 errTextOf 兜底 */
  }
}
